import React, { useState, useEffect } from "react";
import { Card, Typography } from "@material-tailwind/react";
import Popup from "reactjs-popup";
import "../../../../styles/popup.css";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import API from "../../../../utils/API";
import CustomTable from "../../../DynamicComponents/CustomTable";
import EducationDetailsPopUp from "../PopUp/EducationDetailsPopUp";

const AlumniEducationDetails = ({ setBlurActive }) => {
  const [educationDetails, setEducationDetails] = useState(null);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [selectedEducation, setSelectedEducation] = useState(null);
  const [isAddEducation, setIsAddEducation] = useState(false);
  const user = useSelector((state) => state.auth.user) || {};
  const { user_id, department_id } = user;

  const fetchEducationDetails = async () => {
    try {
      const response = await API.get(
        `${department_id}/alumni/education?user_id=${user_id}`
      );
      const details = response?.data;
      if (details && details.length > 0) {
        setEducationDetails(
          details.map((item) => ({
            id: item.edu_id,
            degree: item.degree,
            major: item.major,
            institution: item.institution,
            grade: item.grade,
            start_year: item.start_year,
            end_year: item.end_year,
            details: item.details,
          }))
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Error communicating with server"
      );
    }
  };

  useEffect(() => {
    if (!user_id) return;
    fetchEducationDetails();
  }, [user_id]);

  const openPopup = (education) => {
    setSelectedEducation(education);
    setPopupOpen(true);
    setBlurActive(true);
  };

  const closePopup = () => {
    setPopupOpen(false);
    setIsAddEducation(false);
    setBlurActive(false);
  };

  const handleAddEducationDetails = async (newDetails) => {
    const { degree, major, institution, grade, start_year, end_year, details } =
      newDetails;

    if (!degree || !institution || !start_year || !end_year || !major) {
      toast.error("Please fill all required fields");
      return;
    }

    const payload = {
      degree,
      major,
      institution,
      grade: grade || "",
      start_year,
      end_year,
      details: details || "",
    };

    try {
      let response;
      if (isAddEducation) {
        // POST for new entries
        response = await API.post(
          `${department_id}/alumni/education`,
          payload,
          {
            params: { user_id },
          }
        );
      } else {
        // PUT for updates
        response = await API.put(
          `${department_id}/alumni/education/${selectedEducation.id}`,
          payload,
          {
            params: { user_id },
          }
        );
      }

      if (response?.data) {
        toast.success("Education details successfully saved");
        fetchEducationDetails(); // Refresh the list after adding/updating
        closePopup();
      } else {
        toast.error("Failed to save education details.");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.error ||
          "Error connecting to the server. Please try again."
      );
    }
  };

  const handleDeleteEducationDetails = async (id) => {
    try {
      if (!user_id) {
        toast.error("User ID is required");
        return;
      }

      const response = await API.delete(
        `${department_id}/alumni/education/${id}`,
        {
          params: { user_id },
        }
      );
      if (response && response.data) {
        toast.success("Education details deleted successfully");
        setEducationDetails((prev) => prev.filter((item) => item.id !== id));
      } else {
        toast.error(response.data.message || "Something went wrong");
      }
    } catch (err) {
      console.error("Error deleting education details:", err);
      toast.error(
        err?.response?.data?.error || "Error communicating with server"
      );
    }
  };

  return (
    <>
      <CustomTable
        title="Education Details"
        subtitle="Academic History"
        columns={[
          { key: "degree", label: "Degree" },
          { key: "major", label: "Major" },
          { key: "institution", label: "Institution" },
          { key: "grade", label: "Grade" },
          { key: "start_year", label: "Start Year" },
          { key: "end_year", label: "End Year" },
          { key: "details", label: "Details" },
          { key: "actions", label: "Actions" },
        ]}
        data={educationDetails || []}
        actions={{
          edit: (education) => {
            setIsAddEducation(false);
            setPopupOpen(true);
            setBlurActive(true);
            setSelectedEducation(education);
          },
          delete: (education) => {
            if (education?.id) {
              handleDeleteEducationDetails(education.id);
            } else {
              console.error("Education ID is missing or undefined", education);
              toast.error("Education ID not found");
            }
          },
        }}
        onAdd={() => {
          setIsAddEducation(true);
          setPopupOpen(true);
          setBlurActive(true);
          setSelectedEducation(null);
        }}
      />

      <Popup
        open={isPopupOpen}
        onClose={closePopup}
        className="mx-auto my-auto p-2"
        closeOnDocumentClick
      >
        <div>
          {isAddEducation ? (
            <EducationDetailsPopUp
              degree=""
              major=""
              institution=""
              grade=""
              start_year=""
              end_year=""
              details=""
              closeModal={closePopup}
              handleAddEducationDetails={handleAddEducationDetails}
            />
          ) : (
            selectedEducation && (
              <EducationDetailsPopUp
                degree={selectedEducation.degree}
                major={selectedEducation.major}
                institution={selectedEducation.institution}
                grade={selectedEducation.grade}
                start_year={selectedEducation.start_year}
                end_year={selectedEducation.end_year}
                details={selectedEducation.details}
                closeModal={closePopup}
                handleAddEducationDetails={handleAddEducationDetails}
              />
            )
          )}
        </div>
      </Popup>
    </>
  );
};

export default AlumniEducationDetails;
